import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, ArrowRight, ShieldAlert, RefreshCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { generateCustomScenario, submitIssue } from "@/lib/api";

const PARTNERS = [
  { id: "customer", key: "partnerCustomer" },
  { id: "coworker", key: "partnerCoworker" },
  { id: "supervisor", key: "partnerSupervisor" },
  { id: "patient_visitor", key: "partnerPatient" },
];

// Sensible partner default per named industry so a hospital worker is not
// silently paired with a retail customer. This mirrors the backend's
// _default_partner_for logic; the learner can always change it per exercise.
const PARTNER_DEFAULT_BY_INDUSTRY = {
  hospital_cleaning: "patient_visitor",
  office: "coworker",
  call_center: "customer",
  hospitality: "customer",
  restaurant: "customer",
  retail: "customer",
  travel: "customer",
};

export default function CreateSituation() {
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || null;

  // Effective profile defaults — used only for INITIAL values so we don't
  // overwrite edits the learner has already made. Loading /create directly,
  // refreshing, or navigating from the bottom nav all resolve the same
  // saved context without depending on transient navigation state.
  const savedProfile = user?.work_profile || {};
  const savedIndustry = savedProfile.industry || user?.work_context || null;
  const savedRoleLabel = savedProfile.role_label || "";
  const savedDescription =
    prefill?.description ||
    (savedIndustry === "other" ? (savedProfile.other_description || "") : "");
  const savedPartner =
    prefill?.partner ||
    (Array.isArray(savedProfile.partners) && savedProfile.partners[0]) ||
    PARTNER_DEFAULT_BY_INDUSTRY[savedIndustry] ||
    "customer";
  const savedDifficulty =
    prefill?.difficulty || user?.level || savedProfile.level || "basic";
  const savedRole = prefill?.role_label || savedRoleLabel || "";

  const [description, setDescription] = useState(savedDescription);
  const [roleLabel, setRoleLabel] = useState(savedRole);
  const [partner, setPartner] = useState(savedPartner);
  const [difficulty, setDifficulty] = useState(savedDifficulty);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState("");

  // Track whether the user has manually edited the description or role so a
  // later profile fetch cannot silently overwrite them.
  const editedRef = useRef({ description: false, role: false, partner: false, difficulty: false });

  useEffect(() => {
    // If the profile arrives after mount and the field is still empty AND
    // untouched, populate it. Never overwrite non-empty or edited values.
    if (!editedRef.current.description && !description && savedDescription) {
      setDescription(savedDescription);
    }
    if (!editedRef.current.role && !roleLabel && savedRole) {
      setRoleLabel(savedRole);
    }
    if (!editedRef.current.partner && savedPartner && partner !== savedPartner) {
      // Only apply industry-based default when the learner hasn't touched it.
      setPartner(savedPartner);
    }
    if (!editedRef.current.difficulty && savedDifficulty && difficulty !== savedDifficulty) {
      setDifficulty(savedDifficulty);
    }
    // eslint-disable-next-line
  }, [user?.id, savedIndustry]);

  const doGenerate = async () => {
    if (description.trim().length < 10) {
      toast.error(tr("createSituationHint"));
      return;
    }
    if (generating) return; // dedupe rapid clicks
    setGenerating(true);
    try {
      const r = await generateCustomScenario({
        description: description.trim(),
        role_label: roleLabel.trim() || undefined,
        partner,
        difficulty,
        language: lang,
      });
      setPreview(r);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(err?.response?.data?.detail || tr("errorGeneric"));
      else toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setGenerating(false);
    }
  };

  const doStart = () => {
    if (!preview) return;
    navigate(`/briefing/${preview.id}`);
  };

  const doReport = async () => {
    if (!report.trim() || !preview) return;
    try {
      await submitIssue(report.trim(), `custom_scenario:${preview.id}`);
      toast.success(tr("reportSent"));
      setReport("");
    } catch (err) {
      toast.error(tr("errorGeneric"));
    }
  };

  const sc = preview?.scenario;

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-10 space-y-4" data-testid="create-screen">
        <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="create-title">
          {tr("createRehearseTitle")}
        </h1>
        <p className="text-[12px] text-[rgb(var(--wpe-slate))]" data-testid="create-explain">
          {tr("practiceChoiceExplain")}
        </p>
        <div className="wpe-hint text-[12px]" data-testid="create-hint">
          <ShieldAlert size={12} className="inline mr-1" />
          {tr("createSituationHint")}
        </div>

        <textarea
          className="wpe-input min-h-[110px] resize-none text-[13px]"
          placeholder={tr("createSituationPlaceholder")}
          value={description}
          onChange={(e) => { editedRef.current.description = true; setDescription(e.target.value.slice(0, 600)); }}
          data-testid="create-description"
        />

        <div className="wpe-card space-y-2">
          <label className="wpe-eyebrow block" htmlFor="create-role-label">{tr("createRoleLabel")}</label>
          <input
            id="create-role-label"
            type="text"
            className="wpe-input text-[13px]"
            placeholder={tr("createRolePlaceholder")}
            value={roleLabel}
            onChange={(e) => { editedRef.current.role = true; setRoleLabel(e.target.value.slice(0, 120)); }}
            data-testid="create-role"
          />
          <p className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("createRoleHint")}</p>

          <label className="wpe-eyebrow block pt-2">{tr("createPartnerLabel")}</label>
          <div className="grid grid-cols-2 gap-2">
            {PARTNERS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { editedRef.current.partner = true; setPartner(p.id); }}
                className={`py-2 px-3 rounded-lg border text-[12px] ${partner === p.id ? "border-[rgb(var(--wpe-teal))] bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]" : "border-[rgb(var(--wpe-border))] bg-white text-[rgb(var(--wpe-slate))]"}`}
                data-testid={`create-partner-${p.id}`}
              >
                {tr(p.key)}
              </button>
            ))}
          </div>
          <label className="wpe-eyebrow block mt-2">{tr("onboardingLevel")}</label>
          <div className="flex gap-2 flex-wrap">
            {["basic", "intermediate", "advanced"].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => { editedRef.current.difficulty = true; setDifficulty(d); }}
                className={`text-[11px] px-3 py-1 rounded-full border ${difficulty === d ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`}
                data-testid={`create-difficulty-${d}`}
              >
                {d}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("createExerciseNote")}</p>
        </div>

        <button
          type="button"
          onClick={doGenerate}
          disabled={generating}
          className="wpe-cta w-full"
          data-testid="create-generate-btn"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {preview ? tr("regenerate") : tr("generate")}
        </button>

        {preview && sc && (
          <div className="wpe-card space-y-3" data-testid="create-preview">
            <span className="wpe-eyebrow">{tr("aiCreatedLabel")}</span>
            <div className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">
              {lang === "en" ? sc.title_en : sc.title_es}
            </div>
            <div className="text-[12px] text-[rgb(var(--wpe-slate))]">
              {lang === "en" ? sc.goal_en : sc.goal_es}
            </div>

            <div className="text-[11px] text-[rgb(var(--wpe-slate))] flex flex-wrap gap-x-3 gap-y-1">
              <span data-testid="preview-roles">
                <b className="text-[rgb(var(--wpe-navy))]">{tr("yourRoleLabel")}:</b>{" "}
                {roleLabel || tr("yourRoleGeneric")}
              </span>
              <span data-testid="preview-ai-role">
                <b className="text-[rgb(var(--wpe-navy))]">{tr("aiRoleLabel")}:</b>{" "}
                {tr("partner" + (sc.partner || partner).replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase()))}
              </span>
              <span>· {sc.difficulty} · {sc.time_min}m</span>
            </div>

            {Array.isArray(sc.checkpoints) && sc.checkpoints.length > 0 && (
              <div data-testid="preview-checkpoints">
                <div className="wpe-eyebrow mb-1">{tr("checkpoints")}</div>
                <ul className="space-y-1">
                  {sc.checkpoints.slice(0, 6).map((c) => (
                    <li key={c.id || c.en} className="flex gap-2 text-[12px] text-[rgb(var(--wpe-slate))]">
                      <CheckCircle2 size={13} className="text-[rgb(var(--wpe-teal))] mt-0.5 shrink-0" />
                      <span>{lang === "en" ? c.en : c.es}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(sc.facts) && sc.facts.length > 0 && (
              <div data-testid="preview-facts">
                <div className="wpe-eyebrow mb-1">{tr("facts")}</div>
                <ul className="space-y-1">
                  {sc.facts.slice(0, 6).map((f, i) => (
                    <li key={i} className="text-[12px] text-[rgb(var(--wpe-slate))]">
                      • {lang === "en" ? f.en : f.es}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(sc.phrases) && sc.phrases.length > 0 && (
              <div data-testid="preview-phrases">
                <div className="wpe-eyebrow mb-1">{tr("phrases")}</div>
                <ul className="space-y-1">
                  {sc.phrases.slice(0, 4).map((p, i) => (
                    <li key={i} className="text-[12px]">
                      <span className="text-[rgb(var(--wpe-navy))]">{p.en}</span>
                      <span className="text-[rgb(var(--wpe-muted))] italic"> — {p.es}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="wpe-hint text-[11px]" data-testid="create-notice">{tr("generatedNotice")}</div>

            <button
              type="button"
              className="wpe-cta w-full"
              onClick={doStart}
              data-testid="create-start-btn"
            >
              <ArrowRight size={14} /> {tr("startNow")}
            </button>

            <details className="mt-2">
              <summary className="text-[11px] text-[rgb(var(--wpe-muted))] cursor-pointer" data-testid="create-report-toggle">
                {tr("reportContent")}
              </summary>
              <textarea
                className="wpe-input min-h-[60px] resize-none text-[12px] mt-2"
                placeholder={tr("reportPlaceholder")}
                value={report}
                onChange={(e) => setReport(e.target.value.slice(0, 2000))}
                data-testid="create-report-input"
              />
              <button
                type="button"
                onClick={doReport}
                className="wpe-secondary w-full text-[12px] mt-2"
                data-testid="create-report-submit"
              >
                <RefreshCcw size={12} /> {tr("reportSubmit")}
              </button>
            </details>
          </div>
        )}

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
